import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import { changeLogQuery, type ChangeLogPage, type ChangeLogRow } from '../../api/settingsChangeLog'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import './ChangeLogSettingsPage.css'

/**
 * Settings → Change Log: `views/version-3/change_log/index.php` in varianta
 * `settings = true` (cea care desface randurile), plus purtarea din
 * `public/js/change_log.js` - file, cautare, panoul de filtre, "New",
 * expand/collapse, ancora din adresa.
 *
 * JURNALUL VINE INTREG DE LA SERVER, dintr-o singura cerere: el se citeste din
 * `application/Services/ChangeLog/Release.php`, acelasi fisier pe care il
 * citeste si portalul. Filele sunt acelasi jurnal filtrat, iar cifra de pe
 * fiecare se socoteste din chiar randurile listei - de-aia si vin impreuna.
 *
 * FOILE sunt ale portalului, luate la deschiderea ecranului si scoase la
 * inchidere: `adm.css` (blocul de titlu, ce incarca `loadPlugins = ['adm']`) si
 * `change_log.css` (lista). Nu s-a copiat nicio regula.
 *
 * CE NU E AICI, SI DE CE
 * ----------------------
 * Cutia "Database schema" - oglinda migrarilor Laravel si butonul care aplica
 * schema portalului. Ea se sprijina pe `Platform\Schema` si `Platform\Readiness`,
 * care intreaba baza prin conexiunea CodeIgniter (`table_exists`,
 * `list_fields`), si APLICA DDL. Nu mai e un buton: `Command\SchemaApplyCommand`
 * (`od:schema:apply`) o ruleaza la fiecare livrare, din tag, prin `PortalDb`.
 * Ecranul spune unde e, in locul ei. Pana pe 14 septembrie 2026 nota trimitea
 * la „portalul vechi" cu o legatura catre CHIAR ecranul asta - iar portalul
 * vechi nu mai e pe server.
 *
 * La fel cele doua casute de feedback din marginea din dreapta: `Desk::boxes()`
 * primeste controllerul CodeIgniter ca prim parametru. Se muta cu ecranul lor.
 */

const STYLES = ['css/adm.css', 'css/change_log.css']

/** Presetarile de perioada, ca in macheta. */
const PRESETS: [string, string][] = [
  ['', 'Any time'],
  ['1', 'Today'],
  ['7', '7 days'],
  ['30', '30 days'],
  ['90', '90 days'],
  ['365', 'This year'],
]

type Picked = { sys: string[]; type: string[]; who: string[]; era: string[] }

const NOTHING: Picked = { sys: [], type: [], who: [], era: [] }

/**
 * Ziua de acum N zile, in aceeasi forma `AAAA-LL-ZZ`. "Today" = ziua de azi,
 * deci un pas, nu zero - altfel ar fi insemnat "de la miezul noptii viitoare".
 */
function daysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - (days - 1))
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Cat de demult, in vorbele machetei: "just now", "12 min ago", "3d ago", data. */
function when(value: string): [string, string] {
  const raw = value.trim()
  if (raw === '') return ['', '']
  const at = new Date(raw.replace(' ', 'T'))
  if (Number.isNaN(at.getTime())) return [raw, raw]

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const two = (n: number) => String(n).padStart(2, '0')
  const day = `${two(at.getDate())} ${months[at.getMonth()]} ${at.getFullYear()}`
  const absolute = `${day}, ${two(at.getHours())}:${two(at.getMinutes())}`
  const diff = Math.floor((Date.now() - at.getTime()) / 1000)

  if (diff < 90) return ['just now', absolute]
  if (diff < 3600) return [`${Math.floor(diff / 60)} min ago`, absolute]
  if (diff < 86400) return [`${Math.floor(diff / 3600)}h ago`, absolute]
  if (diff < 86400 * 7) return [`${Math.floor(diff / 86400)}d ago`, absolute]
  return [day, absolute]
}

/**
 * Necititele, din localStorage: "de cand am deschis ultima oara", nu o stare de
 * produs. Numai calendarul intra in numaratoare - livrarile 1.0.x, tichetele
 * inchise si istoria 11.0–18.5 sunt de dinainte, si ar fi facut totul "nou" o
 * singura data.
 *
 * Cheia e cea a portalului (`od-cl-seen-{uid}`), deci cine a citit jurnalul
 * acolo nu-l gaseste tot nou aici.
 */
function seenKey(viewer: number): string {
  return 'od-cl-seen-' + String(viewer || 0)
}

/** Versiunea pana la care omul citise. Un browser care nu tine minte da ''. */
function lastSeen(viewer: number): string {
  try {
    return localStorage.getItem(seenKey(viewer)) ?? ''
  } catch {
    return ''
  }
}

/**
 * Randurile de calendar de dupa versiunea la care omul ramasese.
 *
 * CITIREA E DESPARTITA DE SCRIERE dinadins: daca aceeasi functie ar si citi, si
 * scrie, a doua desenare a paginii (StrictMode deseneaza de doua ori in
 * dezvoltare) ar gasi deja scrisa versiunea cea mai noua si n-ar mai vedea
 * nimic nou. Aici citirea se face o data, in `useState`, iar scrierea intr-un
 * `useEffect` - repetat, el scrie de doua ori acelasi lucru.
 */
function unseenVersions(rows: ChangeLogRow[], seen: string): Set<string> {
  const fresh = new Set<string>()
  for (const row of rows.filter((one) => one.era === 'current')) {
    if (row.version === seen) break
    fresh.add(row.key)
  }

  return fresh
}

/**
 * `settings`: usa lunga (rotita) sau cea scurta (subsol). Pe cea scurta
 * serverul nu cere niciun drept si nu trimite notele; ecranul nu deseneaza
 * nota "Obligatory", cutia de schema, cifra de note, sageata si detaliul -
 * exact ramurile `if ($settings)` din macheta.
 */
export function ChangeLogSettingsPage({ settings = true }: { settings?: boolean }) {
  const page = useQuery(changeLogQuery(settings))
  usePageStyles(STYLES, page.data?.current ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return <ChangeLog page={page.data} />
}

function ChangeLog({ page }: { page: ChangeLogPage }) {
  const [tab, setTab] = useState('all')
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Picked>(NOTHING)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [preset, setPreset] = useState('')
  const [onlyNew, setOnlyNew] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [copied, setCopied] = useState('')
  const search = useRef<HTMLInputElement>(null)

  /* Pana unde citise omul - o singura data, la deschidere. */
  const [seen] = useState(() => lastSeen(page.viewer))
  const fresh = useMemo(() => unseenVersions(page.rows, seen), [page.rows, seen])

  /* ...si de aici incolo a citit tot. Se scrie DUPA ce s-a desenat, ca lista de
     "nou" de mai sus sa fie cea de dinainte de vizita asta. */
  useEffect(() => {
    const newest = page.rows.find((one) => one.era === 'current')?.version ?? ''
    if (newest === '') return
    try {
      localStorage.setItem(seenKey(page.viewer), newest)
    } catch {
      /* Un browser care nu tine minte nu e o eroare a ecranului. */
    }
  }, [page.rows, page.viewer])

  /* Ancora din adresa: ori numele unei file, ori cheia unui rand, care se
     deschide si se aduce sub ochi - ca in `change_log.js`. */
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '')
    if (hash === '') return
    if (hash !== 'all' && page.tabs.some((one) => one.id === hash)) {
      setTab(hash)
      return
    }
    const row = page.rows.find((one) => one.key === hash || one.key === 'v' + hash)
    if (row) {
      setOpen(new Set([row.key]))
      document.getElementById(row.key)?.scrollIntoView({ block: 'center' })
    }
  }, [page.tabs, page.rows])

  /* `/` duce in casuta de cautare, Escape o goleste. */
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? ''
      if (ev.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        ev.preventDefault()
        search.current?.focus()
        return
      }
      if (ev.key === 'Escape') {
        if (document.activeElement === search.current) {
          setQuery('')
          search.current?.blur()
        }
        setPanelOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const needle = query.toLowerCase().replace(/\s+/g, ' ').trim()

  const matches = (row: ChangeLogRow): boolean => {
    if (onlyNew && !fresh.has(row.key)) return false
    if (tab !== 'all' && !row.components.includes(tab)) return false
    if (needle !== '' && !row.search.includes(needle)) return false
    if (picked.sys.length > 0 && !picked.sys.some((one) => row.components.includes(one))) return false
    if (picked.type.length > 0 && !picked.type.some((one) => row.types.includes(one))) return false
    if (picked.who.length > 0 && !picked.who.includes(row.author)) return false
    if (picked.era.length > 0 && !picked.era.includes(row.era)) return false
    if (from !== '' || to !== '') {
      /* Datele sunt scrise `AAAA-LL-ZZ`, deci se compara ca siruri. Un rand
         fara data (istoria veche, care stie doar anul) n-are ce raspunde la o
         intrebare despre perioada: iese din lista. */
      if (row.day === '') return false
      if (from !== '' && row.day < from) return false
      if (to !== '' && row.day > to) return false
    }
    return true
  }

  const shown = page.rows.filter(matches)
  const filterCount = picked.sys.length + picked.type.length + picked.who.length + picked.era.length + (from !== '' || to !== '' ? 1 : 0)

  const toggle = (kind: keyof Picked, value: string) => {
    setPicked((was) => {
      const list = was[kind].includes(value) ? was[kind].filter((one) => one !== value) : [...was[kind], value]
      /* Sistemele bifate iau locul filei: doua controale care spun acelasi
         lucru pe jumatate ar minti amandoua. */
      if (kind === 'sys' && list.length > 0) setTab('all')
      return { ...was, [kind]: list }
    })
  }

  const pickTab = (next: string) => {
    setTab(next)
    /* ...si invers: fila goleste bifele de sistem. */
    setPicked((was) => (was.sys.length > 0 ? { ...was, sys: [] } : was))
  }

  const reset = () => {
    setPicked(NOTHING)
    setFrom('')
    setTo('')
    setPreset('')
  }

  const flip = (key: string) => {
    setOpen((was) => {
      const next = new Set(was)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const shipped = (page.eras.current ?? 0) + (page.eras.deploy ?? 0)
  let lastEra = ''

  return (
    <div id="content">
      <div className="adm-page-head adm-tone-green-mode">
        <span className="adm-page-icon">
          <i className="fas fa-code-branch"></i>
        </span>
        <div className="adm-page-title">
          <div className="adm-page-title-line">
            <h3>Change Log</h3>
          </div>
          <span className="adm-page-note">
            what shipped, when, who, and on which system — one line each, open for the notes
          </span>
        </div>
        <div className="adm-page-stats">
          <Stat value={page.current} label="current" tone="green-mode" />
          <Stat value={String(shipped)} label="ships" tone="blue-hoki" id="od-cl-stat-new" />
          <Stat value={String(page.eras.ticket ?? 0)} label="closed requests" tone="yellow" />
          <Stat value={String(page.eras.history ?? 0)} label="history" tone="grey" />
        </div>
      </div>

      {page.settings ? (
        <>
          <div className="od-cl-note">
            <strong>Obligatory.</strong> This log is filled in code, in{' '}
            <code>application/Services/ChangeLog/Release.php</code>, in the same commit as the change. Date, time,
            who, summary, details. A ship without a row here is incomplete.
          </div>

          <div className="od-cl-note" id="od-cl-schema-note">
            <strong>Database schema.</strong> The portal schema installer (the old <em>Run</em> button) is not a
            button any more: it runs at every ship, from the tag, as{' '}
            <code>php bin/console od:schema:apply --yes</code> in <code>v4/api</code> — the same steps, create-only,
            idempotent. Without <code>--yes</code> it only shows what is pending; <code>--only</code> picks the steps.
          </div>
        </>
      ) : null}

      <div className="od-cl" id="od-cl" data-uid={page.viewer} data-current={page.current}>
        <div className="od-cl-bar" role="region" aria-label="Filter the change log">
          <div className="od-cl-tabs" role="tablist" aria-label="System">
            {page.tabs.map((one) => (
              <button
                key={one.id}
                type="button"
                className={one.id === tab ? 'od-cl-tab is-on' : 'od-cl-tab'}
                role="tab"
                aria-selected={one.id === tab}
                data-tab={one.id}
                onClick={() => pickTab(one.id)}
              >
                <span className="od-cl-tab-name">
                  {one.label} <span className="od-cl-tab-n">{one.count}</span>
                </span>
                {one.version !== '' ? <span className="od-cl-tab-ver">{one.version}</span> : null}
              </button>
            ))}
          </div>

          <div className="od-cl-tools">
            <label className="od-cl-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                id="od-cl-q"
                ref={search}
                placeholder="Search versions, people, notes…"
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>

            <div className="od-cl-filter-wrap">
              <button
                type="button"
                className={filterCount > 0 ? 'od-cl-ghost od-cl-filter is-on' : 'od-cl-ghost od-cl-filter'}
                id="od-cl-filter"
                aria-expanded={panelOpen}
                aria-controls="od-cl-panel"
                title={filterCount === 0 ? 'Filters' : filterCount + ' filters on'}
                onClick={() => setPanelOpen(!panelOpen)}
              >
                <i className="fas fa-filter" aria-hidden="true"></i>
                <span className="od-cl-filter-n" id="od-cl-filter-n" hidden={filterCount === 0}>
                  {filterCount}
                </span>
              </button>

              <div className="od-cl-panel" id="od-cl-panel" hidden={!panelOpen}>
                <div className="od-cl-panel-head">
                  <span>Filters</span>
                  <button type="button" className="od-cl-panel-reset" id="od-cl-reset" onClick={reset}>
                    Reset
                  </button>
                </div>

                <section className="od-cl-panel-sec">
                  <h6>Period</h6>
                  <div className="od-cl-quick">
                    {PRESETS.map(([days, label]) => (
                      <button
                        key={label}
                        type="button"
                        className={days === preset ? 'od-cl-quick-btn is-on' : 'od-cl-quick-btn'}
                        data-days={days}
                        onClick={() => {
                          setPreset(days)
                          setFrom(days === '' ? '' : daysAgo(Number(days)))
                          setTo('')
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="od-cl-dates">
                    <label>
                      From{' '}
                      <input
                        type="date"
                        id="od-cl-from"
                        value={from}
                        onChange={(e) => {
                          setFrom(e.target.value)
                          setPreset('')
                        }}
                      />
                    </label>
                    <label>
                      To{' '}
                      <input
                        type="date"
                        id="od-cl-to"
                        value={to}
                        onChange={(e) => {
                          setTo(e.target.value)
                          setPreset('')
                        }}
                      />
                    </label>
                  </div>
                </section>

                <Checks
                  title="System"
                  kind="sys"
                  values={page.tabs.filter((one) => one.id !== 'all').map((one) => [one.id, one.label])}
                  picked={picked.sys}
                  onPick={toggle}
                />
                {page.filters.types.length > 0 ? (
                  <Checks
                    title="Kind of change"
                    kind="type"
                    values={page.filters.types.map((one) => [one, one])}
                    picked={picked.type}
                    onPick={toggle}
                  />
                ) : null}
                {page.filters.authors.length > 1 ? (
                  <Checks
                    title="Who shipped it"
                    kind="who"
                    values={page.filters.authors.map((one) => [one, one])}
                    picked={picked.who}
                    onPick={toggle}
                    tall
                  />
                ) : null}
                {page.filters.eras.length > 1 ? (
                  <Checks
                    title="Part of the log"
                    kind="era"
                    values={page.filters.eras.map((one) => [one, page.dividers[one]?.name ?? one])}
                    picked={picked.era}
                    onPick={toggle}
                  />
                ) : null}
              </div>
            </div>

            <button
              type="button"
              className={onlyNew ? 'od-cl-chip is-on' : 'od-cl-chip'}
              id="od-cl-newonly"
              aria-pressed={onlyNew}
              hidden={fresh.size === 0}
              onClick={() => setOnlyNew(!onlyNew)}
            >
              New
            </button>
            <button
              type="button"
              className="od-cl-ghost"
              id="od-cl-expand"
              title="Expand all"
              onClick={() => setOpen(new Set(shown.map((row) => row.key)))}
            >
              Expand
            </button>
            <button type="button" className="od-cl-ghost" id="od-cl-collapse" title="Collapse all" onClick={() => setOpen(new Set())}>
              Collapse
            </button>
          </div>
        </div>

        <div className={page.effort.rows.length > 0 ? 'od-cl-split has-rail' : 'od-cl-split'}>
          <div className="od-cl-main">
            <div className="od-cl-list" id="od-cl-list">
              {shown.map((row) => {
                const divider = row.era !== lastEra && page.dividers[row.era]?.title ? page.dividers[row.era] : null
                lastEra = row.era
                const [relative, absolute] = when(row.releasedAt)
                const isOpen = open.has(row.key)

                return (
                  <div key={row.key}>
                    {divider ? (
                      <div className="od-cl-era" data-era-divider={row.era}>
                        <span>{divider.title}</span>
                        <em>{divider.note}</em>
                      </div>
                    ) : null}
                    <article
                      className={fresh.has(row.key) ? 'od-cl-row is-new' : 'od-cl-row'}
                      id={row.key}
                      data-version={row.version}
                      data-era={row.era}
                    >
                      <Line settings={page.settings} isOpen={isOpen} rowKey={row.key} onFlip={() => flip(row.key)}>
                        <span className="od-cl-facts">
                          <span className="od-cl-ver">{row.version}</span>
                          {row.era === 'ticket' ? <span className="od-cl-pill od-cl-pill-ticket">ticket</span> : null}
                          {row.author !== '' ? <span className="od-cl-who">{row.author}</span> : null}
                          <span className="od-cl-when" title={absolute}>
                            {relative}
                          </span>
                          <span className="od-cl-systems">
                            {row.components.map((component) => (
                              <span key={component} className={'od-cl-sys od-cl-sys-' + component}>
                                {component}
                              </span>
                            ))}
                          </span>
                        </span>
                        <span className="od-cl-sum">{row.summary}</span>
                        <span className="od-cl-tail">
                          {row.isCurrent ? <span className="od-cl-pill od-cl-pill-now">current</span> : null}
                          <span className="od-cl-pill od-cl-new">new</span>
                          {row.lines > 0 ? (
                            <span
                              className="od-cl-loc"
                              title={`${row.lines.toLocaleString('en-US')} lines of code changed (added and removed)`}
                            >
                              {row.lines.toLocaleString('en-US')}&nbsp;loc
                            </span>
                          ) : null}
                          {page.settings ? (
                            <>
                              {/* Cifra si sageata promit ceva ce se poate apasa. */}
                              <span className="od-cl-n" title={`${row.notes} notes`}>
                                {row.notes}
                              </span>
                              <span className="od-cl-chevron" aria-hidden="true">
                                <i className="fas fa-chevron-right"></i>
                              </span>
                            </>
                          ) : null}
                        </span>
                      </Line>

                      {page.settings ? (
                      <div className="od-cl-detail" id={row.key + '-detail'} hidden={!isOpen}>
                        <div className="od-cl-detail-inner">
                          <ul>
                            {row.updates.map((update, at) => (
                              <li key={at}>
                                <span className={'od-cl-sys od-cl-sys-' + update.component}>{update.component}</span>
                                {update.type !== '' ? (
                                  <span className={'od-cl-type od-cl-type-' + update.type}>{update.type}</span>
                                ) : null}{' '}
                                {update.details}
                              </li>
                            ))}
                          </ul>
                          <div className="od-cl-detail-meta">
                            {absolute !== '' ? (
                              <span>
                                <i className="far fa-clock" aria-hidden="true"></i> {absolute}
                              </span>
                            ) : null}
                            {row.author !== '' ? (
                              <span>
                                <i className="far fa-user" aria-hidden="true"></i> {row.author}
                              </span>
                            ) : null}
                            <button
                              type="button"
                              className="od-cl-copy"
                              data-copy={row.version}
                              onClick={() => {
                                void navigator.clipboard?.writeText(row.version).then(() => {
                                  setCopied(row.key)
                                  setTimeout(() => setCopied(''), 1200)
                                })
                              }}
                            >
                              {copied === row.key ? 'Copied' : 'Copy version'}
                            </button>
                          </div>
                        </div>
                      </div>
                      ) : null}
                    </article>
                  </div>
                )
              })}
            </div>

            <div className="od-cl-empty" id="od-cl-empty" hidden={shown.length > 0}>
              Nothing in this view. Clear the search or pick another system.
            </div>
          </div>

          {page.effort.rows.length > 0 ? (
            <aside className="od-cl-rail" aria-label="Who shipped what">
              <div className="od-cl-effort">
                <div className="od-cl-effort-head">
                  <span>Who shipped what</span>
                  <em>{page.effort.total} ships</em>
                </div>
                <ul className="od-cl-effort-list">
                  {page.effort.rows.map((one) => (
                    <li key={one.who}>
                      <span className="od-cl-effort-who" title={one.who}>
                        {one.who}
                      </span>
                      <span className="od-cl-effort-bar">
                        <i style={{ width: one.share + '%' }} />
                      </span>
                      <b>{one.ships}</b>
                      {one.recent > 0 ? (
                        <em title="in the last 30 days">+{one.recent}</em>
                      ) : (
                        <em className="is-quiet" title="nothing in the last 30 days">
                          &middot;
                        </em>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="od-cl-effort-foot">
                  One ship = one row in the calendar above. The small number is the last 30 days.
                </p>
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * Randul unei livrari: buton care desface detaliul (rotita) sau un `div`
 * plat (subsol) - `od-cl-line-flat` in macheta: "randul nu mai e buton, si
 * notele nici nu se mai scriu in pagina".
 */
function Line({
  settings,
  isOpen,
  rowKey,
  onFlip,
  children,
}: {
  settings: boolean
  isOpen: boolean
  rowKey: string
  onFlip(): void
  children: ReactNode
}) {
  if (!settings) return <div className="od-cl-line od-cl-line-flat">{children}</div>
  return (
    <button type="button" className="od-cl-line" aria-expanded={isOpen} aria-controls={rowKey + '-detail'} onClick={onFlip}>
      {children}
    </button>
  )
}

function Stat({ value, label, tone, id }: { value: string; label: string; tone: string; id?: string }) {
  return (
    <div className={'adm-stat adm-stat-' + tone}>
      <span className="adm-stat-value" id={id}>
        {value}
      </span>
      <span className="adm-stat-label">{label}</span>
    </div>
  )
}

function Checks({
  title,
  kind,
  values,
  picked,
  onPick,
  tall = false,
}: {
  title: string
  kind: keyof Picked
  values: [string, string][]
  picked: string[]
  onPick(kind: keyof Picked, value: string): void
  tall?: boolean
}) {
  return (
    <section className="od-cl-panel-sec">
      <h6>{title}</h6>
      <div className={tall ? 'od-cl-checks od-cl-checks-tall' : 'od-cl-checks'}>
        {values.map(([value, label]) => (
          <label className="od-cl-check" key={value}>
            <input type="checkbox" data-filter={kind} value={value} checked={picked.includes(value)} onChange={() => onPick(kind, value)} />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </section>
  )
}
